"use client";

import {
  type DropoffPoint,
  type Location,
  type PackageItem,
  type PackagePreset,
  type RouteDropoffPoint,
  type RouteLocation,
  createInitialPackage,
  createNewShipmentItem,
  dropoffPointToLocation,
  formatSlugToLabel,
} from "@/app/encomendas/types";
import {
  readBasePackages,
  stripResumoFields,
} from "@/lib/encomendas/packageStorage";
import {
  clampBoxPayload,
  decryptBoxParam,
  type BoxParamPayload,
} from "@/lib/encomendas/boxParam";
import { DIM_MAXES, MAX_WEIGHT_KG } from "@/app/encomendas/usePackageHandlers";
import { getAuthToken } from "@/utils/authUtils";
import { formatLocalityName } from "@/utils/formatLocality";
import { useParams, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";

const API = process.env.NEXT_PUBLIC_BASE_URL_API;

/** Inteiro sem casas, resto com uma casa — mesmo `fmt1` de usePackageHandlers. */
const fmt1 = (val: number) => (val % 1 === 0 ? String(val) : val.toFixed(1));

export function useEncomendaData() {
  const params = useParams<{ origin?: string; destination?: string }>();
  const searchParams = useSearchParams();

  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [selectedOrigin, setSelectedOrigin] = useState<Location | null>(null);
  const [selectedDestination, setSelectedDestination] =
    useState<Location | null>(null);

  const [routeOriginText, setRouteOriginText] = useState("");
  const [routeDestText, setRouteDestText] = useState("");
  const [routeOrigin, setRouteOrigin] = useState<RouteLocation | null>(null);
  const [routeDest, setRouteDest] = useState<RouteLocation | null>(null);
  const [allRoutes, setAllRoutes] = useState<RouteDropoffPoint[]>([]);
  const [routeLoading, setRouteLoading] = useState(() => {
    if (typeof window === "undefined") return false;
    const p = new URLSearchParams(window.location.search);
    return !!(p.get("origin") && p.get("destination"));
  });

  const [packages, setPackages] = useState<PackageItem[]>([
    createInitialPackage(),
  ]);
  const [packagePresets, setPackagePresets] = useState<PackagePreset[]>([]);
  const [inputValues, setInputValues] = useState<Record<string, string>>({});

  // Volume vindo cifrado na URL (`?box=`), já decifrado e sujeito aos limites.
  // Fica pendente até haver um pacote em que aplicá-lo.
  const [pendingBox, setPendingBox] = useState<BoxParamPayload | null>(null);

  // Fetch package presets
  useEffect(() => {
    const token = getAuthToken();
    if (!token) return;
    fetch(`${API}/api/v1/embarca-log/package_presets`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => data && setPackagePresets(data))
      .catch(() => {});
  }, []);

  // Fetch dropoff points — kept in memory only, never written to localStorage
  useEffect(() => {
    if (typeof window === "undefined") return;
    localStorage.removeItem("embarca-log.dropoff_points");
    fetch(`${API}/api/v1/embarca-log/dropoff_points?operator=1`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (Array.isArray(data)) setAllRoutes(data);
      })
      .catch(() => {});
  }, []);

  // Fetch & cache frequent locations
  useEffect(() => {
    const token = getAuthToken();
    if (!token || typeof window === "undefined") return;
    fetch(`${API}/api/v1/embarca-log/orders/frequent_locations`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data) return;
        const raw = data.data ?? data;
        const toIds = (arr: unknown): string[] => {
          if (!Array.isArray(arr)) return [];
          return arr
            .slice(0, 5)
            .map((x) =>
              x != null && typeof x === "object" && "id" in x
                ? String((x as { id: number }).id)
                : String(x)
            );
        };
        localStorage.setItem(
          "embarca-log.frequent_locations",
          JSON.stringify({
            origin_ids: toIds(raw.origin_ids ?? raw.originIds ?? []),
            destination_ids: toIds(
              raw.destination_ids ?? raw.destinationIds ?? []
            ),
          })
        );
      })
      .catch(() => {});
  }, []);

  // Resolve origin/destination from URL query params
  useEffect(() => {
    const originId = searchParams?.get("origin");
    const destinationId = searchParams?.get("destination");
    if (!originId || !destinationId || typeof window === "undefined") return;

    const applyOrigin = (dp: DropoffPoint) => {
      const loc = dropoffPointToLocation(dp);
      setOrigin(formatLocalityName(loc.name, loc.city_name));
      setSelectedOrigin(loc);
      localStorage.setItem("embarca-log.origin", JSON.stringify(loc));
      if (dp.city_name && dp.state_uf)
        setRouteOrigin({
          id: dp.id,
          name: dp.city_name,
          state: dp.state_uf,
          slug: "",
          ibge_id: dp.ibge_id,
          point_name: dp.name,
        });
    };

    const applyDest = (dp: DropoffPoint) => {
      const loc = dropoffPointToLocation(dp);
      setDestination(formatLocalityName(loc.name, loc.city_name));
      setSelectedDestination(loc);
      localStorage.setItem("embarca-log.destination", JSON.stringify(loc));
      if (dp.city_name && dp.state_uf)
        setRouteDest({
          id: dp.id,
          name: dp.city_name,
          state: dp.state_uf,
          slug: "",
          ibge_id: dp.ibge_id,
          point_name: dp.name,
        });
    };

    // allRoutes (operator 1) is the authoritative list of every dropoff point
    // that can be a valid origin/destination here. Wait for it to load before
    // resolving — otherwise we'd clear the loading state before the route is
    // actually set, flashing an empty map on direct ?origin=&destination= entry.
    if (allRoutes.length === 0) return;

    try {
      const toDP = (p: RouteDropoffPoint): DropoffPoint => ({
        ...p,
        address: p.address ?? undefined,
        number: p.number ?? undefined,
        complement: p.complement ?? undefined,
        district: p.district ?? undefined,
        cep: p.cep ?? undefined,
        phone: p.phone ?? undefined,
        available_hours: p.available_hours ?? undefined,
      });
      const opRaw = allRoutes.find((p) => String(p.id) === originId);
      const dpRaw = allRoutes.find((p) => String(p.id) === destinationId);
      if (opRaw) applyOrigin(toDP(opRaw));
      if (dpRaw) applyDest(toDP(dpRaw));
    } finally {
      // Resolution attempted against the full list — release the loader whether
      // or not both IDs matched (invalid IDs shouldn't hang the screen).
      setRouteLoading(false);
    }
  }, [searchParams, allRoutes]);

  // Apply URL slug params
  useEffect(() => {
    if (!params) return;
    const os = (params as any).origin as string | undefined;
    const ds = (params as any).destination as string | undefined;
    if (os) {
      setOrigin(formatSlugToLabel(os));
      setSelectedOrigin(null);
    }
    if (ds) {
      setDestination(formatSlugToLabel(ds));
      setSelectedDestination(null);
    }
  }, [params]);

  // Persist cupom from URL
  useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      const cupom = searchParams?.get("cupom");
      if (!cupom) return;
      const token = cupom.trim().split(/\s+/)[0].toUpperCase();
      if (token) localStorage.setItem("embarca-log.cupom", token);
    } catch {}
  }, [searchParams]);

  // Restore persisted state from localStorage
  useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      const urlOriginSlug = (params as any)?.origin as string | undefined;
      const urlDestSlug = (params as any)?.destination as string | undefined;

      const rawOrigin = localStorage.getItem("embarca-log.origin");
      if (rawOrigin && !urlOriginSlug) {
        const p = JSON.parse(rawOrigin);
        if ("label" in p) {
          setOrigin(p.label || "");
        } else {
          setOrigin(formatLocalityName(p.name, p.city_name));
          setSelectedOrigin(p);
          if (p.city_name && p.state_uf)
            setRouteOrigin({
              id: p.id,
              name: p.city_name,
              state: p.state_uf,
              slug: "",
              ibge_id: p.ibge_id,
              point_name: p.name,
            });
        }
      }

      const rawDest = localStorage.getItem("embarca-log.destination");
      if (rawDest && !urlDestSlug) {
        const p = JSON.parse(rawDest);
        if ("label" in p) {
          setDestination(p.label || "");
        } else {
          setDestination(formatLocalityName(p.name, p.city_name));
          setSelectedDestination(p);
          if (p.city_name && p.state_uf)
            setRouteDest({
              id: p.id,
              name: p.city_name,
              state: p.state_uf,
              slug: "",
              ibge_id: p.ibge_id,
              point_name: p.name,
            });
        }
      }

      const parsed = readBasePackages<
        PackageItem & {
          weightKg?: number;
          unitValueBRL?: number;
        }
      >();
      if (parsed && parsed.length > 0) {
        const normalized: PackageItem[] = parsed.map((p) => ({
          id: p.id,
          // Nome do pacote é desta etapa (não é campo do Resumo), então volta.
          name: p.name,
          widthCm: p.widthCm ?? 0,
          heightCm: p.heightCm ?? 0,
          lengthCm: p.lengthCm ?? 0,
          selectedPresetId: p.selectedPresetId ?? "",
          // Resumo-owned fields (name/quantity/weightValue/weightUnit) are
          // stripped on purpose: this screen always quotes quantity 1, so a
          // leaked quantity would multiply the quoted weight and value.
          items: p.items?.length
            ? stripResumoFields(p.items)
            : [
                {
                  ...createNewShipmentItem(),
                  weightVolume: p.weightKg ? `${p.weightKg} kg` : "",
                  unitValueBRL: p.unitValueBRL ?? 0,
                },
              ],
        }));
        const restoredInputValues: Record<string, string> = {};
        for (const pkg of normalized) {
          for (const field of ["lengthCm", "widthCm", "heightCm"] as const) {
            if (pkg[field]) {
              restoredInputValues[`${pkg.id}_${field}`] = String(pkg[field]);
            }
          }
        }
        setPackages(normalized);
        setInputValues(restoredInputValues);
      }
    } catch {}
  }, [params]);

  // Decifra o `box` da URL. Roda uma vez por token: a decifragem é assíncrona,
  // então o resultado fica em `pendingBox` e é aplicado no efeito seguinte,
  // quando já existe um pacote (o restore do localStorage pode trocar o id).
  useEffect(() => {
    const raw = searchParams?.get("box");
    if (!raw) return;

    let cancelled = false;
    decryptBoxParam(raw).then((box) => {
      if (cancelled || !box) return;
      // Mesmos limites da digitação manual — o payload é entrada de usuário,
      // podendo ter sido forjado (o segredo é público por vir no bundle).
      setPendingBox(clampBoxPayload(box, DIM_MAXES, MAX_WEIGHT_KG));
    });

    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  // Aplica o volume no primeiro pacote. Depois do restore do localStorage de
  // propósito: o `box` é a intenção mais recente do usuário (ele acabou de
  // preencher na landing page) e deve vencer o que ficou guardado.
  useEffect(() => {
    if (!pendingBox || packages.length === 0) return;

    const target = packages[0];
    const { lengthCm, widthCm, heightCm, weightKg, unitValueBRL } = pendingBox;

    setPackages((prev) =>
      prev.map((pkg, index) => {
        if (index !== 0) return pkg;
        const items = pkg.items.length ? pkg.items : [createNewShipmentItem()];
        return {
          ...pkg,
          lengthCm,
          widthCm,
          heightCm,
          // As medidas vieram avulsas, não de um preset salvo.
          selectedPresetId: "",
          items: items.map((item, itemIndex) =>
            itemIndex === 0
              ? {
                  ...item,
                  quantity: 1,
                  weightValue: weightKg,
                  weightUnit: "kg" as const,
                  weightVolume: weightKg ? `${fmt1(weightKg)} kg` : "",
                  unitValueBRL,
                }
              : item
          ),
        };
      })
    );

    // O efeito de sync abaixo só preenche campo ainda não rastreado, então um
    // valor restaurado do localStorage sobreviveria. Sobrescreve explicitamente.
    setInputValues((prev) => ({
      ...prev,
      [`${target.id}_lengthCm`]: lengthCm ? fmt1(lengthCm) : "",
      [`${target.id}_widthCm`]: widthCm ? fmt1(widthCm) : "",
      [`${target.id}_heightCm`]: heightCm ? fmt1(heightCm) : "",
    }));

    setPendingBox(null);
  }, [pendingBox, packages]);

  // Sync inputValues from packages when a field is not yet tracked
  useEffect(() => {
    setInputValues((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const pkg of packages) {
        for (const field of ["lengthCm", "widthCm", "heightCm"] as const) {
          const key = `${pkg.id}_${field}`;
          if (!next[key] && pkg[field]) {
            next[key] = String(pkg[field]);
            changed = true;
          }
        }
      }
      return changed ? next : prev;
    });
  }, [packages]);

  // True while a direct ?origin=&destination= entry is still resolving — used to
  // hold the full-screen loader until both envio and retirada are set. Released
  // once the resolution attempt completes (routeLoading clears), so invalid IDs
  // fall through to the normal empty state instead of hanging the loader.
  const hasRouteParams = !!(
    searchParams?.get("origin") && searchParams?.get("destination")
  );
  const awaitingRouteParams = hasRouteParams && routeLoading;

  return {
    // route
    awaitingRouteParams,
    origin,
    setOrigin,
    destination,
    setDestination,
    selectedOrigin,
    setSelectedOrigin,
    selectedDestination,
    setSelectedDestination,
    routeOriginText,
    setRouteOriginText,
    routeDestText,
    setRouteDestText,
    routeOrigin,
    setRouteOrigin,
    routeDest,
    setRouteDest,
    allRoutes,
    routeLoading,
    // packages
    packages,
    setPackages,
    packagePresets,
    inputValues,
    setInputValues,
  };
}
